import type { ComponentType, CSSProperties, ReactNode } from 'react';
import { Check, Download } from 'lucide-react';

import { detailCta } from './detail-cta';

/** Glyph shown in place of a thumbnail. */
type FileGlyph = ComponentType<{ className?: string; strokeWidth?: number }>;

/**
 * Attributes forwarded verbatim onto the download anchor. Restricted to
 * `data-*` so a consumer cannot reach in and rewrite the anchor's href, target
 * or rel, which the row owns. `undefined` means "no attribute" — React omits
 * it, which is what a row wants for an id a generated document does not have.
 */
type AnchorDataAttributes = Record<
    `data-${string}`,
    string | number | undefined
>;

/**
 * How the row's download control behaves. The two modes are mutually exclusive
 * by construction, and neither can be chosen without the thing it needs in
 * order to act: `link` without an `href`, or `button` without an `onDownload`,
 * does not typecheck. Omitting the prop is the only way to get no control, so
 * "renders a control that does nothing" is not expressible.
 */
export type DownloadControl =
    | {
          kind: 'link';
          /**
           * The file's destination, rendered as a real `href`. An anchor is
           * what gives the row middle-click, Cmd-click, "Copy link address",
           * "Save link as", the hover URL preview and crawler visibility —
           * every one of which a button plus `window.open` throws away.
           */
          href: string;
          /**
           * Data attributes placed on the anchor, e.g. the tracker's
           * `data-track-download` set, which the delegated `mousedown`
           * listener in `useLogLinkClicks` reads to log `DownloadsClick`.
           */
          dataAttributes?: AnchorDataAttributes;
          /**
           * Side effect on activation, e.g. flipping the downloaded tick. The
           * navigation is the anchor's own, so this must not open the file and
           * must not preventDefault.
           */
          onActivate?: () => void;
      }
    | {
          kind: 'button';
          /**
           * Handles the download itself — opening the file, or taking the
           * click over with a gate or a modal. Used where there is no plain
           * URL to hand the browser.
           */
          onDownload: () => void;
      };

export interface DownloadFileRowProps {
    /** File name, shown as the row's heading. */
    title: string;
    /** Sub-line under the title, and the "File uploaded by" column's value. */
    uploader: string;
    /**
     * The "File" column's value — the file's category, or a fixed label. Pass
     * an empty string when the file has no category: the cell then prints
     * nothing at all, heading included, rather than a "File" heading over a
     * blank. An empty cell is only laid out while the column is rendered, and
     * it then holds its width so the rows around it stay in line; where the
     * column is dropped there is no cell, and this value goes unread.
     */
    fileLabel: string;
    /**
     * Whether the desktop row lays out a "File" column at all. This is a
     * decision about the list, not about the row: when no file in the list is
     * categorised the column is nothing but a reserved 255px void on every
     * row, so the owner drops it for all of them. The row itself is not
     * resized by that — it fills its container either way, and the width the
     * column gives up goes to the flexible spacer that sits between the last
     * column and the download pill, so the pill stays where it was. The
     * default keeps the column, which is what a list where only some files are
     * categorised needs — the blank cells are what keep the rows aligned with
     * each other.
     */
    showFileColumn?: boolean;
    /**
     * Whether the desktop row lays out a "File uploaded by" column. It repeats
     * the sub-line already printed under the title, so a list whose every row
     * names the same uploader is printing the same word twice per row and
     * spending 260px on it. Drop it there; keep it where the uploader varies
     * row to row. Default keeps the column.
     */
    showUploaderColumn?: boolean;
    /** The "Filetype" column, e.g. `PDF` or `Link`. */
    format: string;
    /** The "Size" column, already formatted; pass an em dash when unknown. */
    size: string;
    /** Glyph used on mobile, and on desktop when there is no thumbnail. */
    Icon: FileGlyph;
    /** Desktop-only thumbnail; the glyph stands in when absent. */
    thumbnailSrc?: string;
    /** Shows a tick beside the title once the file has been actioned. */
    hasDownloaded?: boolean;
    /**
     * The download affordance. Omit it when the file has nowhere to go: the
     * row then renders no pill at all, rather than a focusable control that
     * does nothing.
     */
    control?: DownloadControl;
    /**
     * A second action, shown beside the download control. Kept as a slot rather
     * than a set of save props: the row does not know what saving means, and the
     * page that owns the file does.
     */
    saveControl?: ReactNode;
    /** Entrance animation, applied to the row's outer element. */
    style?: CSSProperties;
}

/**
 * A stacked label/value cell in the desktop row. A cell with no value keeps its
 * width, so the columns after it stay in line, but prints nothing: a heading
 * over a blank reads as a column that failed to load.
 */
const DownloadField = ({
    label,
    value,
    className,
}: {
    label: string;
    value: string;
    className?: string;
}) => (
    <div className={`min-w-0 shrink-0 ${className ?? ''}`}>
        {value !== '' && (
            <>
                <p className="m-0 font-sans text-[0.875rem] leading-tight text-[#767676]">
                    {label}
                </p>
                <p
                    className="m-0 mt-1.5 truncate font-sans text-[1rem] leading-tight font-medium text-[#141414]"
                    title={value}
                >
                    {value}
                </p>
            </>
        )}
    </div>
);

/**
 * The revamped detail pages' download row (Figma 1846:9068): thumbnail or file
 * glyph, the title beside its uploader, then the documented-by / file / format
 * / size columns and the download affordance. Mobile collapses to an editorial
 * hairline row carrying the same title and format/size.
 *
 * Purely presentational — gating, tracking and link handling belong to the
 * page that owns the file, which passes the outcome in as `control`. The
 * download pill is the only control: it occupies its own box, so a click on
 * the row background does nothing.
 *
 * On desktop the row is a white card on a white section background, so at rest
 * a hairline ring is the only thing separating it from the page and from the
 * row above and below; the hover and focus-within shade and the thumbnail tile
 * are both toned off white so they still read against the card. The ring is
 * inset and drawn with box-shadow, so it costs no height and neither the row's
 * footprint nor the gap between rows moves.
 *
 * Forced-colors mode (Windows High Contrast) discards box-shadow and repaints
 * the background in system colours, so the inset ring goes and the desktop
 * rows would read as one undivided block. Under forced colours — and nowhere
 * else — the row is given a real border instead, so the boundary survives; it
 * costs a pixel of height at the top and at the bottom, which is cheaper than
 * losing the boundary. Mobile needs none of this: its separator is already a
 * border, which forced colours keeps.
 */
export const DownloadFileRow = ({
    title,
    uploader,
    fileLabel,
    showFileColumn = true,
    showUploaderColumn = true,
    format,
    size,
    Icon,
    thumbnailSrc,
    hasDownloaded = false,
    control,
    saveControl,
    style,
}: DownloadFileRowProps) => {
    // The pill is the control and its hit area is its own box. The row itself
    // cannot be the control: an anchor or button takes phrasing content only,
    // and this row is built from divs, paragraphs and an image. Keeping the
    // columns outside the control also leaves them readable, where a row-wide
    // aria-label spoke over them.
    //
    // Without a `control` the row is a listing and nothing more: no pill, so no
    // tab stop and no hover or focus affordance promising a download the file
    // cannot deliver.
    const pillClassName = detailCta(
        'filled-light',
        'h-9 shrink-0 cursor-pointer gap-1.5 px-4 text-sm',
    );
    const pillContent = (
        <>
            <Download className="size-3.5" strokeWidth={2} aria-hidden />
            Download
        </>
    );

    let downloadPill: ReactNode = null;
    if (control?.kind === 'link') {
        downloadPill = (
            // `noreferrer` as well as `noopener`: download hosts are third
            // parties and have no business being told which page sent the
            // visitor.
            <a
                {...control.dataAttributes}
                href={control.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={control.onActivate}
                aria-label={`Download ${title}`}
                className={pillClassName}
            >
                {pillContent}
            </a>
        );
    } else if (control?.kind === 'button') {
        downloadPill = (
            <button
                type="button"
                onClick={control.onDownload}
                aria-label={`Download ${title}`}
                className={pillClassName}
            >
                {pillContent}
            </button>
        );
    }

    return (
        <div
            className="group block w-full text-left md:overflow-hidden md:rounded-lg md:bg-white md:inset-ring-1 md:inset-ring-black/10 md:transition-colors md:focus-within:bg-[#f0f0f2] md:hover:bg-[#f0f0f2] md:forced-colors:border md:forced-colors:border-[color:CanvasText]"
            style={style}
        >
            {/* Desktop: thumbnail + title + columns + actions. */}
            <div className="hidden items-center gap-[1.5rem] px-5 py-[0.625rem] md:flex">
                {/* Tinted and hairlined so the tile still reads as a tile on
                    the white row, whatever the thumbnail's own backdrop. */}
                <div className="flex size-[5rem] shrink-0 items-center justify-center overflow-hidden rounded border border-black/[0.08] bg-[#f6f6f6]">
                    {thumbnailSrc ? (
                        <img
                            src={thumbnailSrc}
                            alt=""
                            loading="lazy"
                            className="size-full object-contain"
                        />
                    ) : (
                        <Icon
                            className="h-6 w-6 text-black/30"
                            strokeWidth={1.5}
                        />
                    )}
                </div>
                {/* Shrinkable, unlike the fixed columns after it: where the row has
                    room the flexible spacer takes up the slack and this keeps its
                    full width, and where it does not the title gives way rather
                    than pushing the download control off the end. */}
                <div className="flex w-[24.875rem] min-w-0 flex-col gap-[2px]">
                    <div className="flex min-w-0 items-center gap-1.5">
                        <p
                            className="m-0 min-w-0 truncate font-sans text-[1rem] leading-tight font-medium text-[#141414]"
                            title={title}
                        >
                            {title}
                        </p>
                        {hasDownloaded && (
                            <Check
                                className="text-green-500 h-3.5 w-3.5 shrink-0 animate-tick-pop-in"
                                strokeWidth={3}
                            />
                        )}
                    </div>
                    <p
                        className="m-0 truncate font-sans text-[0.875rem] leading-tight text-[#6e6e6e]"
                        title={uploader}
                    >
                        {uploader}
                    </p>
                </div>
                {showUploaderColumn && (
                    <DownloadField
                        label="File uploaded by"
                        value={uploader}
                        className="w-[16.25rem]"
                    />
                )}
                {showFileColumn && (
                    <DownloadField
                        label="File"
                        value={fileLabel}
                        className="w-[15.9375rem]"
                    />
                )}
                <DownloadField
                    label="Filetype"
                    value={format}
                    className="w-[12.9375rem]"
                />
                <DownloadField
                    label="Size"
                    value={size}
                    className="w-[7.625rem]"
                />
                <div className="flex-1" />
                {saveControl}
                {downloadPill}
            </div>

            {/* Mobile: editorial hairline row — a neutral file glyph, the title
                with its format/size, and the download affordance. No coloured
                file-type badge or filled card. */}
            <div className="flex items-center gap-3.5 border-b border-black/10 py-3.5 group-last:border-b-0 md:hidden">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-[#f4f4f5]">
                    <Icon className="size-4 text-[#767676]" strokeWidth={1.5} />
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <div className="flex min-w-0 items-center gap-1.5">
                        <p
                            className="m-0 min-w-0 truncate font-sans text-sm leading-tight font-medium text-[#181818]"
                            title={title}
                        >
                            {title}
                        </p>
                        {hasDownloaded && (
                            <Check
                                className="text-green-500 h-3.5 w-3.5 shrink-0 animate-tick-pop-in"
                                strokeWidth={3}
                            />
                        )}
                    </div>
                    <p className="m-0 font-sans text-xs leading-tight text-[#8a8a91]">
                        {format}
                        {size !== '—' ? ` · ${size}` : ''}
                    </p>
                </div>
                {saveControl}
                {downloadPill}
            </div>
        </div>
    );
};
